import {
  Box,
  Breadcrumbs,
  Chip,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  Link as RouterLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import {
  RiCheckboxBlankCircleLine,
  RiCheckboxCircleFill,
} from "@remixicon/react";
import React, { useEffect, useState } from "react";
import DeliveryHistoryTable from "../../components/Delivery/DeliveryHistoryTable";
import DeliveryScheduleTable from "../../components/Delivery/DeliveryScheduleTable";
import SchemaConfigTable from "../../components/Schema/SchemaConfigTable";
import { useError } from "../../contexts/ErrorContext";
import useApi from "../../hooks/useGaneshApi";
import { Delivery_T } from "../../types/Delivery";
import { formatDateHumanReadable } from "utils/formatting";

const DeliveryDetailPage: React.FC = () => {
  const { deliveryId } = useParams<{ deliveryId: string }>();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedTabName, setSelectedTabName] = useState("History");

  const { showError } = useError();
  const { apiRequest } = useApi();

  const {
    data: deliveryMetadata,
    isLoading: metadataLoading,
    isError,
    error,
  } = useQuery<Delivery_T, Error>({
    queryKey: ["delivery", deliveryId],
    queryFn: async () => {
      const response = await apiRequest<Delivery_T>(
        `/v1/deliveries/${deliveryId}`
      );
      if (response.error) {
        throw new Error(response.error);
      }
      if (!response.data) {
        throw new Error(`Delivery with ID ${deliveryId} not found.`);
      }
      return response.data;
    },
    enabled: !!deliveryId,
  });

  useEffect(() => {
    const tabName = location.pathname.split("/").pop();
    switch (tabName) {
      case "history":
        setSelectedTab(0);
        setSelectedTabName("History");
        break;
      case "schedule":
        setSelectedTab(1);
        setSelectedTabName("Schedule");
        break;
      case "schema":
        setSelectedTab(2);
        setSelectedTabName("Schema");
        break;
      default:
        setSelectedTab(0);
        setSelectedTabName("History");
    }
  }, [location.pathname]);

  useEffect(() => {
    document.title = `${
      deliveryMetadata?.delivery_name || "Delivery"
    } - ${selectedTabName}`;
  }, [deliveryMetadata, selectedTabName]);

  if (isError) {
    showError(
      `Failed to fetch delivery metadata for ${deliveryId}: ${error.message}`
    );
  }

  const getStatusIcon = (isActive?: boolean) => {
    return isActive ? (
      <RiCheckboxCircleFill color="green" />
    ) : (
      <RiCheckboxBlankCircleLine color="grey" />
    );
  };

  if (metadataLoading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" color="text.secondary">
          Loading delivery details...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <RouterLink
          to="/customers"
          style={{ textDecoration: "none", color: "inherit" }}
        >
          Customers
        </RouterLink>
        <RouterLink
          to={`/customer/${deliveryMetadata?.customer_id}/deliveries`}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          {deliveryMetadata?.customer_name} Deliveries
        </RouterLink>
        <Typography color="text.primary">
          {deliveryMetadata?.delivery_name}
        </Typography>
      </Breadcrumbs>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ flexGrow: 1 }}
        >
          {deliveryMetadata?.delivery_name} (ID: {deliveryId})
        </Typography>
        <Chip
          icon={getStatusIcon(deliveryMetadata?.is_active)}
          label={deliveryMetadata?.is_active ? "Active" : "Inactive"}
          variant="outlined"
          sx={{ ml: 2 }}
        />
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Typography>
            <strong>Region(s):</strong> {deliveryMetadata?.country ?? "N/A"}
          </Typography>
          <Typography>
            <strong>Delivery type(s):</strong>{" "}
            {deliveryMetadata?.delivery_type ?? "N/A"}
          </Typography>
          <Typography>
            <strong>Fulfillment destination:</strong>{" "}
            {deliveryMetadata?.fulfillment_destination ?? "N/A"}
          </Typography>
          <Typography>
            <strong>Fulfillment group(s):</strong>{" "}
            {deliveryMetadata?.fulfillment_groups ?? "N/A"}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography>
            <strong>First delivery date:</strong>{" "}
            {deliveryMetadata?.first_delivery_date
              ? formatDateHumanReadable(
                  new Date(deliveryMetadata.first_delivery_date)
                )
              : "N/A"}
          </Typography>
          <Typography>
            <strong>Final scheduled delivery date:</strong>{" "}
            {deliveryMetadata?.final_scheduled_delivery
              ? formatDateHumanReadable(
                  new Date(deliveryMetadata.final_scheduled_delivery)
                )
              : "N/A"}
          </Typography>
          <Typography>
            <strong>License end date:</strong>{" "}
            {deliveryMetadata?.license_end_date
              ? formatDateHumanReadable(
                  new Date(deliveryMetadata.license_end_date)
                )
              : "N/A"}
          </Typography>
          <Typography>
            <strong>Data format:</strong>{" "}
            {deliveryMetadata?.customer_file_type ?? "N/A"}
          </Typography>
        </Grid>
      </Grid>
      <Tabs
        value={selectedTab}
        onChange={(e, newValue) => setSelectedTab(newValue)}
        sx={{ marginBottom: 2 }}
      >
        <Tab
          label="History"
          component={RouterLink}
          to={`/delivery/${deliveryId}/history`}
        />
        <Tab
          label="Schedule"
          component={RouterLink}
          to={`/delivery/${deliveryId}/schedule`}
        />
        <Tab
          label="Schema"
          component={RouterLink}
          to={`/delivery/${deliveryId}/schema`}
        />
      </Tabs>
      <Routes>
        <Route
          path="/"
          element={
            <Navigate to={`/delivery/${deliveryId}/history`} replace={true} />
          }
        />
        <Route
          path="history"
          element={<DeliveryHistoryTable deliveryId={deliveryId} />}
        />
        <Route
          path="schedule"
          element={<DeliveryScheduleTable deliveryId={deliveryId} />}
        />
        <Route
          path="schema"
          element={
            <SchemaConfigTable datasetId={deliveryId} datasetType="delivery" />
          }
        />
      </Routes>
    </Box>
  );
};

export default DeliveryDetailPage;
